import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Plus, Loader2, CheckCircle2, XCircle, ArrowUp, ArrowDown, ClipboardList, History, Circle, ArrowRight, PlusCircle, Download, Search } from "lucide-react";

async function downloadCsv(path, filename) {
  const { data } = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const CRIT_CLS = {
  critical: "border-red-500/40 bg-red-500/10 text-red-600",
  high: "border-orange-500/40 bg-orange-500/10 text-orange-600",
  medium: "border-amber-500/40 bg-amber-500/10 text-amber-600",
  low: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
};

const VENDOR_STATUSES = ["Active", "Under Review", "Offboarded"];

function OffboardingChecklistDialog({ vendor, onClose, onChanged }) {
  const { toast } = useToast();
  const [items, setItems] = useState(vendor.offboarding_checklist || []);
  const [busyId, setBusyId] = useState(null);

  const toggle = async (itemId) => {
    if (busyId) return; // guard against a second click slipping in before the disabled state commits
    setBusyId(itemId);
    try {
      const current = items.find((i) => i.id === itemId);
      const { data } = await api.put(`/compliance/vendors/${vendor.id}/offboarding-checklist/${itemId}`, { done: !current.done });
      setItems(data.offboarding_checklist || []);
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't update checklist", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyId(null); }
  };

  const doneCount = items.filter((i) => i.done).length;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-vendor-offboarding-dialog">
        <DialogHeader>
          <DialogTitle>{vendor.name} — Offboarding checklist</DialogTitle>
          <DialogDescription>Work through these steps to make sure access and data cleanup aren't missed ({doneCount}/{items.length} done).</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1">
          {items.map((item) => (
            <label key={item.id} className="flex items-start gap-2.5 text-[12.5px] cursor-pointer rounded-lg border border-border px-2.5 py-2" data-testid={`compliance-vendor-checklist-item-${item.id}`}>
              <Checkbox checked={item.done} disabled={!!busyId} onCheckedChange={() => toggle(item.id)} className="mt-0.5" />
              <span className={cn(item.done && "line-through text-muted-foreground")}>{item.label}</span>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-vendor-offboarding-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HistoryEventRow({ event }) {
  const when = new Date(event.at).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
  let icon = <Circle className="w-3.5 h-3.5 text-muted-foreground" />;
  let text = null;
  if (event.type === "created") {
    icon = <PlusCircle className="w-3.5 h-3.5 text-primary" />;
    text = <>Vendor added</>;
  } else if (event.type === "status_change") {
    icon = <ArrowRight className="w-3.5 h-3.5 text-primary" />;
    text = <>Status changed from <strong>{event.from_status || "—"}</strong> to <strong>{event.to_status}</strong></>;
  } else if (event.type === "checklist_item") {
    icon = event.done
      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
      : <XCircle className="w-3.5 h-3.5 text-muted-foreground" />;
    text = <>{event.done ? "Checked off" : "Unchecked"} offboarding step: <span className="italic">{event.item_label}</span></>;
  }
  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-border last:border-0" data-testid={`compliance-vendor-history-event-${event.id}`}>
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px]">{text}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">{event.actor_name} · {when}</div>
      </div>
    </div>
  );
}

function VendorHistoryDialog({ vendor, onClose }) {
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/compliance/vendors/${vendor.id}/history`);
        if (active) setEvents(data.history || []);
      } catch { /* noop */ } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [vendor.id]);

  const exportHistory = async () => {
    setExporting(true);
    try {
      await downloadCsv(
        `/compliance/vendors/${vendor.id}/history/export.csv`,
        `${vendor.name.replace(/[^A-Za-z0-9._-]+/g, "-")}-audit-trail-${new Date().toISOString().slice(0, 10)}.csv`,
      );
    } catch (e) {
      toast({ title: "Export failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setExporting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="compliance-vendor-history-dialog">
        <DialogHeader>
          <DialogTitle>{vendor.name} — Audit trail</DialogTitle>
          <DialogDescription>Every status change and offboarding step completed, for audit evidence.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="h-24 rounded-lg bg-muted/40 animate-pulse" />
          ) : events.length === 0 ? (
            <div className="text-[12px] text-muted-foreground py-4">No history yet.</div>
          ) : (
            events.map((e) => <HistoryEventRow key={e.id} event={e} />)
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="gap-1.5"
            onClick={exportHistory}
            disabled={exporting || events.length === 0}
            data-testid="compliance-vendor-history-export-btn"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Export CSV
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="compliance-vendor-history-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddVendorModal({ open, onClose, onSaved }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [category, setCategory] = useState("SaaS");
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast({ title: "Name is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/vendors", { name, category, owner: owner || null });
      toast({ title: "Vendor added" });
      setName(""); setOwner("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't add vendor", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-vendor-modal">
        <DialogHeader>
          <DialogTitle>New vendor</DialogTitle>
          <DialogDescription>Add a third-party vendor to track in the compliance register.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" autoFocus data-testid="compliance-vendor-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1" data-testid="compliance-vendor-category-input" />
            </div>
            <div>
              <Label>Owner</Label>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1" data-testid="compliance-vendor-owner-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-vendor-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-vendor-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Adding…" : "Add vendor"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ComplianceVendors({ isAdmin }) {
  const { toast } = useToast();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [sortDir, setSortDir] = useState("desc"); // default: highest risk first
  const [reviewingId, setReviewingId] = useState(null);
  const [statusSavingId, setStatusSavingId] = useState(null);
  const [checklistVendor, setChecklistVendor] = useState(null);
  const [historyVendor, setHistoryVendor] = useState(null);
  const [exportingAll, setExportingAll] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/compliance/vendors");
      setVendors(data.vendors || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    let arr = [...vendors];
    if (statusFilter !== "all") arr = arr.filter((v) => v.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter((v) => `${v.name} ${v.category} ${v.owner || ""}`.toLowerCase().includes(q));
    }
    arr.sort((a, b) => sortDir === "desc" ? b.risk_score - a.risk_score : a.risk_score - b.risk_score);
    return arr;
  }, [vendors, sortDir, search, statusFilter]);

  const toggleSort = () => setSortDir((d) => (d === "desc" ? "asc" : "desc"));

  const exportAllHistory = async () => {
    setExportingAll(true);
    try {
      await downloadCsv(
        "/compliance/vendors/history/export.csv",
        `vendor-audit-trail-all-${new Date().toISOString().slice(0, 10)}.csv`,
      );
    } catch (e) {
      toast({ title: "Export failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setExportingAll(false); }
  };

  const markReviewed = async (v) => {
    setReviewingId(v.id);
    try {
      await api.put(`/compliance/vendors/${v.id}`, { last_reviewed_at: new Date().toISOString().slice(0, 10) });
      toast({ title: `${v.name} marked as reviewed today` });
      load();
    } catch (e) {
      toast({ title: "Couldn't mark reviewed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setReviewingId(null); }
  };

  const changeStatus = async (v, status) => {
    setStatusSavingId(v.id);
    try {
      const { data } = await api.put(`/compliance/vendors/${v.id}`, { status });
      toast({ title: `${v.name} marked ${status}` });
      await load();
      if (status === "Offboarded" && data.offboarding_checklist) {
        setChecklistVendor(data);
      }
    } catch (e) {
      toast({ title: "Couldn't update status", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setStatusSavingId(null); }
  };

  if (loading) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-3" data-testid="compliance-vendors-page">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search vendors…" className="pl-8 h-9" data-testid="compliance-vendors-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="compliance-vendors-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {VENDOR_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={exportAllHistory}
          disabled={exportingAll || vendors.length === 0}
          data-testid="compliance-vendor-export-all-history-btn"
        >
          {exportingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Export all audit trails
        </Button>
        {isAdmin && (
          <Button size="sm" className="gap-1.5" onClick={() => setModalOpen(true)} data-testid="compliance-vendor-add-btn">
            <Plus className="w-3.5 h-3.5" /> New vendor
          </Button>
        )}
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table data-testid="compliance-vendors-table">
          <TableHeader>
            <TableRow>
              <TableHead>Vendor</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Criticality</TableHead>
              <TableHead>Data shared</TableHead>
              <TableHead>SOC 2 on file</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>
                <button
                  type="button"
                  onClick={toggleSort}
                  className="flex items-center gap-1 hover:text-foreground"
                  data-testid="compliance-vendor-risk-sort"
                >
                  Risk score
                  {sortDir === "desc" ? <ArrowDown className="w-3 h-3" /> : <ArrowUp className="w-3 h-3" />}
                </button>
              </TableHead>
              <TableHead>Last reviewed</TableHead>
              <TableHead>Next review</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((v) => (
              <TableRow key={v.id} data-testid={`compliance-vendor-row-${v.id}`}>
                <TableCell className="text-[12.5px] font-medium">
                  {v.name}
                  <button
                    type="button"
                    onClick={() => setHistoryVendor(v)}
                    className="flex items-center gap-1 text-[10.5px] font-normal text-muted-foreground hover:text-primary mt-0.5"
                    data-testid={`compliance-vendor-history-btn-${v.id}`}
                  >
                    <History className="w-3 h-3" /> History
                  </button>
                </TableCell>
                <TableCell className="text-[12px] text-muted-foreground">{v.category}</TableCell>
                <TableCell><span className={cn("text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5", CRIT_CLS[v.criticality])}>{v.criticality}</span></TableCell>
                <TableCell className="text-[11.5px] text-muted-foreground max-w-[200px] truncate">{v.data_shared || "—"}</TableCell>
                <TableCell>{v.soc2 ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}</TableCell>
                <TableCell className="text-[12px]">{v.owner || "—"}</TableCell>
                <TableCell data-testid={`compliance-vendor-risk-${v.id}`}>
                  <span className={cn("text-[11px] font-semibold rounded-full border px-2 py-0.5", CRIT_CLS[v.risk_level])}>{v.risk_score}</span>
                </TableCell>
                <TableCell className="text-[12px] text-muted-foreground">
                  {v.last_reviewed_at || "—"}
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => markReviewed(v)}
                      disabled={reviewingId === v.id}
                      className="block text-[10.5px] font-medium text-primary hover:underline mt-0.5 disabled:opacity-50"
                      data-testid={`compliance-vendor-mark-reviewed-${v.id}`}
                    >
                      {reviewingId === v.id ? "Saving…" : "Mark reviewed"}
                    </button>
                  )}
                </TableCell>
                <TableCell className="text-[12px] text-muted-foreground">{v.next_review || "—"}</TableCell>
                <TableCell>
                  {isAdmin ? (
                    <Select value={v.status} onValueChange={(val) => changeStatus(v, val)} disabled={statusSavingId === v.id}>
                      <SelectTrigger className="h-7 w-[130px] text-[12px]" data-testid={`compliance-vendor-status-select-${v.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VENDOR_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-[12px]">{v.status}</span>
                  )}
                  {v.status === "Offboarded" && v.offboarding_checklist && (
                    <button
                      type="button"
                      onClick={() => setChecklistVendor(v)}
                      className="flex items-center gap-1 text-[10.5px] font-medium text-primary hover:underline mt-1"
                      data-testid={`compliance-vendor-checklist-link-${v.id}`}
                    >
                      <ClipboardList className="w-3 h-3" />
                      {v.offboarding_checklist.filter((i) => i.done).length}/{v.offboarding_checklist.length} offboarded
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-[12.5px] text-muted-foreground py-8" data-testid="compliance-vendors-empty">No vendors match.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <AddVendorModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
      {checklistVendor && (
        <OffboardingChecklistDialog vendor={checklistVendor} onClose={() => setChecklistVendor(null)} onChanged={load} />
      )}
      {historyVendor && (
        <VendorHistoryDialog vendor={historyVendor} onClose={() => setHistoryVendor(null)} />
      )}
    </div>
  );
}
